import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CartService } from '@b2b/features/cart/core/cart.service';
import { CartEntry, CartItem, GetCartEntryResponse } from '@b2b/features/cart/utilities/cart.model';
import { PRODUCT_MEDIA_TYPE } from '@b2b/features/product/core/utilities/product.model';
import { ProductImageService } from '@b2b/features/product/core/services/product-image.service';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, filter, take } from 'rxjs';
import { InputQuantityComponent } from '@shared/components/form-elements/input-quantity/input-quantity.component';
import { MediaComponent } from '@shared/components/media/media.component';
import { ModalConfirmService } from '@shared/components/modal-confirm/modal-confirm.service';

@Component({
  selector: 'cart-product',
  imports: [CommonModule, RouterModule, ReactiveFormsModule, TranslateModule, MediaComponent, InputQuantityComponent],
  templateUrl: './cart-product.component.html',
  styleUrl: './cart-product.component.scss',
})
export class CartProductComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cartService = inject(CartService);
  private readonly modalConfirmService = inject(ModalConfirmService);
  private readonly productImageService = inject(ProductImageService);

  // Inputs
  entry = input.required<CartEntry | GetCartEntryResponse>();
  disabled = input(false);
  onCheckout = input(true);
  hideQuantity = input(false);

  showStrikethrough = computed(() => {
    const entry = this.entry();
    const pricing = entry.pricingData;
    if (!pricing) return false;
    return pricing.finalPrice < pricing.price;
  });

  isPaymentMethodUnavailable = computed(() => !this.entry().pricingData);
  entryImage = computed(() =>
    this.productImageService.getMediaObject(
      this.entry(),
      PRODUCT_MEDIA_TYPE.CART_ICON,
      this.entry().productName ?? '',
    ),
  );

  taxItems = computed(() => {
    const entry = this.entry();
    const pricing = entry.pricingData;

    return [
      {
        label: 'ICMS',
        value: entry.icms?.value ?? pricing?.icms ?? 0,
        formattedValue:
          entry.icms?.formattedValue ?? pricing?.formatedICMS ?? 'R$ 0,00',
      },
      {
        label: 'IPI',
        value: entry.ipi?.value ?? pricing?.ipi ?? 0,
        formattedValue:
          entry.ipi?.formattedValue ?? pricing?.formatedIPI ?? 'R$ 0,00',
      },
      {
        label: 'ST',
        value: entry.st?.value ?? pricing?.st ?? 0,
        formattedValue:
          entry.st?.formattedValue ?? pricing?.formatedST ?? 'R$ 0,00',
      },
    ].filter((tax) => tax.value > 0);
  });

  paymentConditionId = new FormControl('');
  quantity = new FormControl(0);

  constructor() {
    effect(() => {
      if (this.disabled()) {
        this.quantity.disable({ emitEvent: false });
      } else {
        this.quantity.enable({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    const entry = this.entry();

    this.quantity.setValue(entry.qty ?? 0, { emitEvent: false });

    this.onUpdateCartEntry();
  }

  confirmDeleteProduct(): void {
    const entry = this.entry();

    if (entry.entryNumber === undefined) return;

    const entryNumber = entry.entryNumber;
    const description = entry.productName ?? '';

    const cartCode = this.cartService.getCartID();

    this.modalConfirmService
      .open({
        title: `Carrinho #${cartCode}`,
        text: `Tem certeza que deseja remover o item <b>${description}</b>?`,
        confirmButtonText: 'Remover',
        cancelButtonText: 'Cancelar',
      })
      .pipe(take(1))
      .subscribe(({ confirmed }) => {
        if (confirmed) {
          this.cartService.deleteCartItem(entryNumber, cartCode);
        }
      });
  }

  private updateCartEntry(): void {
    const entry = this.entry();
    const qty = this.quantity.value ?? 0;

    if (entry.entryNumber === undefined || qty <= 0) return;

    const cartItem: CartItem = {
      warehouse: entry.warehouseCode || '',
      productCode: entry.productId || entry.productCode || '',
      qty: Number(qty),
    };

    this.cartService.updateCartItem(entry.entryNumber, cartItem);
  }

  private onUpdateCartEntry(): void {
    this.quantity.valueChanges
      .pipe(
        filter(value => value !== null),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.updateCartEntry();
      });
  }

  formatPaymentConditions() {
    const pricing = this.entry().pricingData;
    if (!pricing) return [];

    // Retorna formato simplificado baseado no pricing atual
    return [
      {
        value: `${pricing.formatedFinalPrice} | Desc: ${pricing.formatedDiscount}`,
        code: 'default',
      },
    ];
  }
}
